import {SOCKET_CLIENT_VERSION,
    SOCKET_CLOSE_KICKED,
    SOCKET_CLOSE_MISSING_AUTH,
    SOCKET_CLOSE_REQUIRE_TURNSTILE_TOKEN,
    SOCKET_CLOSE_SERVER_TOO_SLOW,
    type SocketData, type SocketEvent,
} from '@colonist/types'
import {UIUtilsEjsVariables} from '../UIUtilsEjsVariables.ts'
import {logDebugInfo, logError} from '../ui_utils/Utils.ts'
import * as MsgPack from '@msgpack/msgpack'
import {type NetworkEnums} from '../../../../shared/GameEnums.ts'
import {StorageWrapper} from '../StorageWrapper.ts'
import {getHomepageUrl, navigateToPage} from '../ui_utils/ClientUtils.ts'
import {CA} from '../analytics/Analytics.ts'
import {createDeferredPromise, type DeferredPromise, TimeUtils} from '@colonist/utils'
import {UITurnstile} from '../network/UITurnstile.ts'

export class SocketClient {
    private readonly serverUrl: string
    private webSocket: WebSocket | undefined
    private sendTurnstileToken: boolean = false
    private firstUserSessionId: string | undefined

    private readonly onSessionEstablished: () => void
    private readonly onStateUpdate: (state: NetworkEnums.State, data: any) => void
    private readonly onClose: () => void

    constructor(serverUrl: string, onSessionEstablished: () => void, onStateUpdate: (state: NetworkEnums.State, data: any) => void, onClose: () => void) {
        this.serverUrl = serverUrl
        this.onSessionEstablished = onSessionEstablished
        this.onStateUpdate = onStateUpdate
        this.onClose = onClose
    }

    isSocketOpen(): boolean {
        return this.webSocket?.readyState === WebSocket.OPEN
    }

    async connect(): Promise<'connected' | 'retry' | 'permanentlyFailed'> {
        const resultPromise = createDeferredPromise<'connected' | 'retry' | 'permanentlyFailed'>()

        const connectTimeout = 9 * TimeUtils.MILLISECONDS
        setTimeout(() => {
            if(!resultPromise.pending) return

            this.close(SOCKET_CLOSE_SERVER_TOO_SLOW)
            resultPromise.resolve('retry')
        }, connectTimeout)

        this.webSocket = new WebSocket(await this.getWebSocketUrl())
        this.webSocket.binaryType = 'arraybuffer'

        this.webSocket.onmessage = (message) => this.handleInitialWebSocketMessage(message, resultPromise)

        this.webSocket.onclose = (ev) => {
            switch(ev.code) {
                case SOCKET_CLOSE_KICKED:
                case SOCKET_CLOSE_MISSING_AUTH:
                    resultPromise.resolve('permanentlyFailed')
                    break
                case SOCKET_CLOSE_REQUIRE_TURNSTILE_TOKEN:
                    this.sendTurnstileToken = true
                    resultPromise.resolve('retry')
                    break
                default:
                    resultPromise.resolve('retry')
                    break
            }
            this.handleWebSocketClose(ev.code)
        }

        this.webSocket.onerror = (error) => {
            this.handleWebSocketError(error)
            resultPromise.resolve('retry')
        }

        return resultPromise
    }

    private async getWebSocketUrl(): Promise<string> {
        const queryString = new URLSearchParams()
        queryString.append('version', SOCKET_CLIENT_VERSION)

        if(this.firstUserSessionId != undefined) {
            queryString.append('firstUserSessionId', this.firstUserSessionId)
        }

        if(UIUtilsEjsVariables.isUserOnDiscordMobile) {
            const authToken = StorageWrapper.getAuthToken()
            if(authToken != null) queryString.append('authToken', authToken)
        }

        if(this.sendTurnstileToken) {
            queryString.append('turnstileToken', await UITurnstile.getToken())
        }

        return `${this.serverUrl}?${queryString}`
    }

    private handleInitialWebSocketMessage(message: MessageEvent, connectionPromise: DeferredPromise<string>) {
        if(this.webSocket == undefined) return // TS only

        try {
            const socketEvent: SocketEvent = JSON.parse(message.data)
            switch(socketEvent.type) {
                case 'Connected':
                    if(this.firstUserSessionId == undefined) {
                        this.firstUserSessionId = socketEvent.userSessionId
                    }
                    return
                case 'SessionEstablished':
                    this.webSocket.onmessage = (normalMessage) => this.handleWebSocketMessage(normalMessage)
                    this.onSessionEstablished()
                    connectionPromise.resolve('connected')
                    this.sendTurnstileToken = false
                    return
            }
        } catch(error: any) {
            // eslint-disable-next-line no-console
            console.error('Parse websocket initial message error', message, error)
        }

        this.handleWebSocketMessage(message)
    }

    private handleWebSocketMessage(message: MessageEvent) {
        const dataObject = MsgPack.decode(message.data) as SocketData
        if(UIUtilsEjsVariables.socketDebugActive) {
            logDebugInfo('SocketMessage', [
                '======',
                `Message type: ${dataObject.id}, data:`,
                dataObject.data,
            ])
        }

        this.onStateUpdate(Number(dataObject.id) as NetworkEnums.State, dataObject.data)
    }

    private handleWebSocketError(error: Event) {
        if(UIUtilsEjsVariables.socketDebugActive) logError('connect', ['Closing socket, encountered error: ', error])
        this.webSocket?.close()
    }

    private handleWebSocketClose(code: number) {
        CA.sendGoogleAnalyticsEvent(CA.Category.General, CA.Label.GeneralBrokenInternetConnection)

        if(code == SOCKET_CLOSE_KICKED) {
            navigateToPage(getHomepageUrl())
            return
        }

        this.onClose()
    }

    close(code?: number) {
        this.webSocket?.close(code)
    }

    send(binarySocketMessage: Uint8Array) {
        if(!this.isSocketOpen()) return
        this.webSocket?.send(binarySocketMessage)
    }
}
